import React, { useState, useEffect, useReducer, useContext } from 'react';
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert'
import { getSelectedLang, getSelectedYear, getSelectedFarmId } from '../../common';
import Spinner from 'react-bootstrap/Spinner';
import '../../css/general.css'
import '../../css/inventory.css';
import { getContentWithAction, postContentWithAction } from '../../apiConnect';
import AppContext from '../../AppContext';
import Row from 'react-bootstrap/Row';
import Carousel from 'react-bootstrap/Carousel';
import EventSection from './EventSection';
import StorageSection from './StorageSection';
import ProductInfoSection from './ProductInfoSection';
import ExtraInfoSection from './ExtraInfoSection';
import { EventActionType } from '../../Constants/StorageConstants';

const PurchaseListModal = (props) => {
    const { t } = useTranslation();
    const { auth } = useContext(AppContext);
    const alert = useAlert();

    /* STATES */                                      
    const [isLoading, setIsLoading]                   = useState(false);
    const [saveInProgress, setSaveInProgress]         = useState(false);
    const [hasChanges, setHasChanges]                 = useState(false);
    const [activeIndex, setActiveIndex]               = useState(0);
    const [saveActivated, setSaveActivated]           = useState(false);
    const [enableSave, setEnableSave]                 = useState(false);
    const [nextSectionAllowed, setNextSectionAllowed] = useState(true);

    const [productSectionData, setProductSectionData] = useState({});

    const [LAST_INDEX, setLastIndex] = useState(2);

    // * Context object for storage/inventory purchase
    const defaultPurchaseObject = {
        purchaseObject: {
        },
        sectionValidation: {
            storageSection: false,
            eventSection: false,
            productInfoSection: false,
            extraInfoSection: false
        }
    };

    const purchaseReducer = (state, action) => {
        switch (action.type) {
            case 'UPDATE':
                return {
                    purchaseObject: {
                        ...state.purchaseObject,
                        ...action.purchaseObject
                    },
                    sectionValidation: {
                        ...state.sectionValidation,
                        ...action.sectionValidation
                    }
                };
            default:
                return state;
        }
    }
    const [purchaseObject, dispatch] = useReducer(purchaseReducer, defaultPurchaseObject);

    useEffect(() => {
        retrieveModalData();
    }, []);

    // * Check for validity of sections to enable next button
    useEffect(() => {
        // Check section validations
        let validStorages = purchaseObject.sectionValidation.storageSection;
        let validEvent = purchaseObject.sectionValidation.eventSection;
        let validProductInfo = purchaseObject.sectionValidation.productInfoSection;
        let validExtraInfo = purchaseObject.sectionValidation.extraInfoSection;

        // Check current page index
        switch (activeIndex)
        {
            case 0:
                setNextSectionAllowed(validStorages && validEvent);
                break;
            case 1:
                setNextSectionAllowed(validProductInfo);
                break;
            case LAST_INDEX:
                setNextSectionAllowed(false);
                setEnableSave(validStorages && validEvent && validProductInfo && validExtraInfo);
                break;
            default:
                setNextSectionAllowed(false);
                break;
        }
    });

    // * Retrieve modal data
    const retrieveModalData = () => {
        var farmId = getSelectedFarmId();
        var year = getSelectedYear();
        var lang = getSelectedLang();

        setIsLoading(true);

        Promise.all([
            getContentWithAction(auth.getAccessToken(), 'session', 'storeAllowed', {}),
            getContentWithAction(auth.getAccessToken(), 'plotOperation', 'getMMWisuStorages', { farmId, year, lang }),
            getContentWithAction(auth.getAccessToken(), 'resource', 'plants', {}),
            getContentWithAction(auth.getAccessToken(), 'resource', 'fertilizers', {}),
            getContentWithAction(auth.getAccessToken(), 'resource', 'pesticides', {}),
            getContentWithAction(auth.getAccessToken(), 'resource', 'storages', {}),
            getContentWithAction(auth.getAccessToken(), 'resource', 'organicProductionTypes', {}),
        ]).then(([session, mmwisustorages, plants, fertilizers, pesticides, storages, organicPTypes]) => {
            var sectionData = {
                session: session,
                mmwisuStorages: mmwisustorages,
                plants: plants,
                fertilizers: fertilizers,
                pesticides: pesticides,
                storages: storages,
                organicPTypes: organicPTypes
            };
            setProductSectionData(sectionData);
            setIsLoading(false);
        }).catch((c) => { setIsLoading(false); });
    }

    // Save changes
    const saveChanges = () => {

        const constructPurchasePropertyObject = (pObject) => {
            var products = [];
            const data = pObject.purchaseObject;
            // Construct products to save
            var saveObject = {
                //StorageSection
                operationType: data.operationType,

                // EventSection
                actionType: data.siloTrasnfer ? EventActionType.FromSilo : data.eventType,
                additionalInfo: data.eventExplanation,
                purchaseDate: data.purchaseDate,
                storage: data.siloId,
                location: data.location,
                productCode: data.productId,

                // ProductInfoSection
                varietyId: data.varietyId,
                plantId: data.plantId,
                quality: data.seedQualityId,
                fertilizerId: data.fertilizerId,
                pesticideId: data.pesticideId,
                /*locationId: data.locationId,*/
                organic: data.organicStatusId,
                amount: data.amount,
                purchasePrice: data.batchPrice,

                // ExtraInfoSection
                seller: data.seller,
                sellerInformation: data.sellerContact,
                supplier: data.supplier,
                supplierInformation: data.supplierContact,
                inspection: data.deliveryChecked,
                extraInfo: data.extraInfo
            };

            products.push(saveObject);
            return products;
        }

        setSaveInProgress(true);

        postContentWithAction(auth.getAccessToken(), 'plotOperation', 'savePurchase', { models: constructPurchasePropertyObject(purchaseObject) }).then(result => {
            setSaveInProgress(false);
            alert.show(t('SavedSuccesfully'), { type: 'success' });
            props.onHide();
            props.refresh();
        }).catch((c) => {
            // show alert
            setSaveInProgress(false);
            alert.show(t('SavedFailed'), { type: 'error' });
        });
    }

    const handleSectionChange = (selectedIndex) => {
        if (selectedIndex === LAST_INDEX) setSaveActivated(true);
        else setSaveActivated(false);
        setActiveIndex(selectedIndex);
    }

    const nextDisabled = () => {
        if (activeIndex === LAST_INDEX) return true;
        return !nextSectionAllowed;
    }

    return (
        <>
            <Modal show={props.show} onHide={props.onHide}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-dark">{t('PurhcaseList_Add')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="purchase-list-modal">
                    {isLoading ? <Spinner as="span" className="modal-spinner" animation="border" role="status" variant="secondary" /> :
                        <Carousel className="purchase-list-carousel" activeIndex={activeIndex} variant="dark" interval={null} indicators={false} touch={false} controls={false} wrap={false}>
                            <Carousel.Item className="purchase-list-carousel-item">
                                <Row className="purchaselist-section">
                                    <StorageSection
                                        updatePurchaseObject={dispatch}
                                    />
                                </Row>
                                <Row className="purchaselist-section">
                                    <EventSection
                                        updatePurchaseObject={dispatch}
                                        purchaseListObject={purchaseObject}
                                        storages={productSectionData.storages}
                                        mgmtData={productSectionData.mmwisuStorages}
                                    />
                                </Row>
                            </Carousel.Item>
                            <Carousel.Item className="purchase-list-carousel-item">
                                <Row className="purchaselist-section">
                                    <ProductInfoSection
                                        auth={auth}
                                        lang={getSelectedLang()}
                                        sectionData={productSectionData}
                                        updatePurchaseObject={dispatch}
                                        purchaseListObject={purchaseObject}
                                    />
                                </Row>
                            </Carousel.Item>
                            <Carousel.Item className="purchase-list-carousel-item">
                                <Row className="purchaselist-section">
                                    <ExtraInfoSection
                                        updatePurchaseObject={dispatch}
                                        purchaseListObject={purchaseObject}
                                    />
                                </Row>
                            </Carousel.Item>
                        </Carousel>
                    }
                </Modal.Body>
                <Modal.Footer className="modal-footer-right-align">
                    <div className="page-indicator">{t('Page')} : {(activeIndex + 1) + '/' + (LAST_INDEX + 1)}</div>
                    <Button variant="outline-secondary close-button" onClick={props.onHide}>{t('Close')}</Button>
                    <Button className="btn-carousel-control-left" disabled={activeIndex === 0} hidden={activeIndex === 0} onClick={() => {
                        setActiveIndex(activeIndex - 1);
                        handleSectionChange(activeIndex - 1);
                    }}>{'<'}{t('Previous')}</Button>
                    <Button className="btn-carousel-control-left" disabled={nextDisabled()} hidden={saveActivated} onClick={() => {
                        setActiveIndex(activeIndex + 1);
                        handleSectionChange(activeIndex + 1);
                    }}>{t('Next')}{'>'}</Button>
                    {saveActivated ?
                        <Button variant="success" disabled={!enableSave || saveInProgress || isLoading} className="btn-show-more" onClick={() => { saveChanges(); }}>{saveInProgress ? <Spinner as="span" animation="border" size="sm" role="status" variant="secondary" /> : t('Save')}</Button>
                        : ''}
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default PurchaseListModal;